import * as React from 'react';
import * as cx from 'classnames';

import SuperCritical from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';
import { EventType } from 'data/models/EventType';
import { PortCallChangeType } from 'server/enums/PortCallChangeType';
import { LineTimeActualField } from 'server/enums/PortCallChange';
import { PersonProfilePage, NotificationViewModel, EventViewModel, PortCallChangeViewModel } from './model';

import { ActionsMenuButton } from 'portcall/actionsmenubutton/view';
import { PhoneNumberView } from 'portcall/phonenumber/view';
import { SelectProfileImageView } from 'portcall/selectprofileimage/view';
import { ChangePasswordView } from 'core/changepassword/view';
import { UserSelectorView } from 'portcall/userselector/view';
import { ValidationErrorView } from 'core/validationerror/view';
import { BookingRateView } from 'portcall/bookingrate/view';
import { PersonProfilePopupView } from 'portcall/personprofilepopup/view';
import { PCDismissible } from 'portcall/pcdismissible/view';
import { PCModal } from 'portcall/pcmodal/view';

export let PersonProfilePageView = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div className="personprofilepage pc-page-fixedwidth">
        <div className="pc-actions-links-header">
            <a className="pc-back-link" href="" onClick={noDefault(m.GoBack)}>&lt;&lt; Back</a>
            <ActionsMenuButton onClick={m.ToggleActionsMenu} />
            <ActionsMenu model={m} />
        </div>
        <Header model={m} />
        {(user => user && <SelectProfileImageView modelContainer={user.ImageSelector} />)(m.User())}
        <PageBody model={m} />
        <PageModals model={m} />
    </div>
);

let Header = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div className="profile-header">
        <img src={m.User()?.Model.Image.ProfileUrl()} />
        <div className="profile-header-details">
            <div className="name pc-text-fixedwidth">{m.User()?.Model.FullName()}</div>
            <div className="org-info">
                {(o => o && 
                <a className="pc-text-fixedwidth" href={'#!orgprofile?id=' + o.Id()}>
                    {o.Name()}
                </a>
                )(m.User()?.Model.Organization())}
                <span className="pc-icon-bullet"></span>
                <span className="pc-text-fixedwidth">{m.User()?.Model.JobDescription()}</span>
            </div>
        </div>
    </div>
);

let PageBody = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div hidden={!!m.User()?.ImageSelector()}>
        <PageLeftSide model={m} />
        <PageRightSide model={m} />
    </div>
);

let PageLeftSide = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div className="left-side">
        <div className="pc-card profile-body clearfix">
            <div className="pc-card-header">
                <span className="pc-card-title">Profile</span>
            </div>
            <div className="left-column">
                <UserDetails model={m} />
                <LandingPageView model={m} />
                <UIThemeView model={m} />
                <PortCallEditorsView model={m} />
            </div>
            <div className="right-column">
                <BookingRateView bookingRate={m.Model.BookingRate} />
                <RolesView model={m} />
            </div>
        </div>
    </div>
);

let RolesView = SuperCritical(({ model: m } : { model: PersonProfilePage }) =>
    <div className="roles">
        <h3 className="profile-section-title">What can I do?</h3>
        {m.Roles().map(r =>
            <div className="role" key={r.Model.Id()}>
                <div className="role-name">
                    <span className="glyphicon glyphicon-ok pc-text-accent"></span>
                    {' '}
                    <span>{r.Model.Name()}</span>
                </div>
                <div className="role-description pc-text-smalllabel">
                    <span>{r.Description()}</span>
                    {' '}
                    <a href="" onClick={noDefault(() => r.ShowAll(true))} hidden={r.ShowAll()}>More</a>
                    <a href="" onClick={noDefault(() => r.ShowAll(false))} hidden={!r.ShowAll()}>Less</a>
                </div>
            </div>
        )}
    </div>
);

let UserDetails = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div className={cx('user-details', {
            'pc-show-editcontrols': m.Editing(),
            'pc-show-viewcontrols': !m.Editing()
        })}>
        <h3 className="profile-section-title">Details</h3>
        <a href="" className="pc-input-viewcontrol" onClick={noDefault(() => m.Editing(true))}>Edit</a>
        <a href="" className="pc-input-editcontrol" onClick={noDefault(() => m.Editing(false))}>Done Editing</a>
        {(user => user &&
            <div className="editprofileimagelink pc-input-editcontrol">
                <a href="" onClick={noDefault(user.ShowSelectProfileImage)}>
                    Edit Profile Image
                </a>
            </div>
        )(m.User())}
        <UserNameView model={m} />
        <UserEmailView model={m} />
        <UserOrganizationView model={m} />
        <UserJobDescriptionView model={m} />
        <UserPhoneNumbersView model={m} />
    </div>
);

export let UserNameView = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div className="details-row">
        <label className="left-col">Name</label>
        <div className="right-col">
            <span className="pc-input-viewcontrol pc-text-fixedwidth">
                {m.User()?.Model.FullName()}
            </span>
            {(user => user &&
                <span>
                    <input className="pc-input-editcontrol firstname"
                            type="text"
                            placeholder="First name"
                            value={user.Model.FirstName()}
                            onChange={e => user.Model.FirstName((e.target as HTMLInputElement).value)} />
                    <input className="pc-input-editcontrol lastname"
                            type="text"
                            placeholder="Last name"
                            value={user.Model.LastName()}
                            onChange={e => user.Model.LastName((e.target as HTMLInputElement).value)} />
                    <ValidationErrorView error={() => user.Validation()['FirstName']}  />
                    <ValidationErrorView error={() => user.Validation()['LastName']} />
                </span>
            )(m.User())}
        </div>
    </div>
);

export let UserEmailView = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div className="email details-row">
        <label className="left-col">Email</label>
        <div className="right-col">
            <a
                className="pc-input-viewcontrol pc-text-fixedwidth"
                href={'mailto:' + (m.User()?.User()?.Model.Email() ?? '')}
            >
                {m.User()?.User()?.Model.Email()}
            </a>
            {(user => {
                if (!user) return;
                let _u = user.User();
                return (
                    <span>
                        {_u &&
                            <input className="pc-input-editcontrol"
                                    type="text"
                                    placeholder="Email"
                                    value={_u.Model.Email() || ''}
                                    onChange={e => _u!.Model.Email((e.target as HTMLInputElement).value)} />
                        }
                        <a href="" className="notification-settings-link pc-input-editcontrol" onClick={noDefault(user.ShowNotificationSettings)}>
                            Set notification email
                        </a>
                        {_u && <ValidationErrorView error={() => _u!.Validation()['Email']} />}
                    </span>
                );
            })(m.User())}
        </div>
    </div>
);

export let UserOrganizationView = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div className="organization details-row">
        <label className="left-col">Organization</label>
        <div className="right-col">
            {(o => o && 
                <a className="pc-text-fixedwidth" href={'#!orgprofile?id=' + o.Id()}>
                    {o.Name()}
                </a>
            )(m.User()?.Model.Organization())}
        </div>
    </div>
);

export let UserJobDescriptionView = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div className="job-description details-row">
        <label className="left-col">Job Description</label>
        <div className="right-col">
            <span className="pc-input-viewcontrol pc-text-fixedwidth">{m.User()?.Model.JobDescription()}</span>
            {(user => user && 
                <span>
                    <input className="pc-input-editcontrol pc-text-fixedwidth"
                            type="text"
                            placeholder="Job description"
                            value={user.Model.JobDescription() || ''}
                            onChange={e => user.Model.JobDescription((e.target as HTMLInputElement).value)} />
                    <ValidationErrorView error={() => user.Validation()['JobDescription']} />
                </span>
            )(m.User())}
        </div>
    </div>
);

export let UserPhoneNumbersView = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div className="contacts details-row">
        <label className="left-col">Contacts</label>
        {(user => user &&
            <div className="right-col">
                <div className="link-shell">
                    <a href="" className="pc-input-editcontrol profile-add-link" onClick={noDefault(user.PhoneNumbers.Add)}>+Add</a>
                </div>
                <div className="phonenumbers">
                    {user.PhoneNumbers.Numbers().map(phoneNumber =>
                        <PhoneNumberView key={phoneNumber.Model.Id()} model={phoneNumber} />
                    )}
                </div>
            </div>
        )(m.User())}
    </div>
);

export let LandingPageView = SuperCritical(({ model: m } : { model : PersonProfilePage }) => 
    <div className="landing-page">
        <h3 className="profile-section-title my-landing-page">My Landing Page</h3>
        {(u => u && 
            <select
                className="pc-select"
                value={u.Model.LandingPage() ?? ''}
                onChange={e => u.Model.LandingPage(e.currentTarget.value)}
            >
                {m.Model.LandingPages().map(lp =>
                    <option key={lp.Hash() ?? ''} value={lp.Hash() ?? ''}>{lp.Name()}</option>
                )}
            </select>
        )(m.User())}
        <span className="pc-select-chevron"></span>
    </div>
);

export let UIThemeView = SuperCritical(({ model: m } : { model : PersonProfilePage }) => 
    <div className="ui-theme">
        <h3 className="profile-section-title ui-theme">My Visual Theme</h3>
        {(u => u && 
            <select
                className="pc-select"
                value={u.Model.UIThemeId() + ''}
                onChange={e => u.Model.UIThemeId(+e.currentTarget.value || null)}
            >
                {m.UIThemes().map(t => {
                    const def = t === u.Model.DefaultUITheme();
                    return (
                        <option key={t.Id()} value={def ? 'null' : t.Id()}>{`${def ? '(default) ' : ''}${t.Name()} - ${t.Description()}`}</option>
                    );
                })}
            </select>
        )(m.User())}
        <span className="pc-select-chevron"></span>
    </div>
);

export let PortCallEditorsView = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div className={cx('portcall-editors', {
                'pc-show-editcontrols': !!m.User()?.PortCallEditors.Editing(),
                'pc-show-viewcontrols':  !m.User()?.PortCallEditors.Editing()
            })}>
        <h3 className="profile-section-title">
            Who can edit my PortCalls?
            {' '}
            {m.User() ? '(' + m.User()!.PortCallEditors.Editors().length + ')' : ''}
        </h3>
        {(user => user &&
            <div className="edit-link">
                <a href="" className="pc-input-viewcontrol" onClick={noDefault(() => user.PortCallEditors.Editing(true))}>Edit</a>
                <a href="" className="pc-input-editcontrol" onClick={noDefault(() => user.PortCallEditors.Editing(false))}>Done Editing</a>       
            </div>
        )(m.User())}
        {(user => user &&
            <div>
                <div className="pc-input-editcontrol">
                    <UserSelectorView model={user.PortCallEditors.NewEditorSelector}
                        placeholder="Invite Editor..." />
                </div>
                {user.PortCallEditors.Editors().map(editor =>
                    editor.Editor() &&
                        <div className="portcall-editor" key={editor.Editor()!.Id()}>
                            <a href="" className="pc-input-editcontrol delete-btn" onClick={noDefault(editor.Remove)}>
                                <span className="pc-close-icon"></span>
                            </a>
                            {' '}
                            <img src={editor.Editor()!.Image.ThumbUrl()} />
                            <a href="" className="name-text pc-text-fixedwidth pc-text-smalltext" onClick={noDefault(editor.ToggleProfile)}>
                                {editor.Editor()!.FullName()}
                            </a>
                            <PersonProfilePopupView modelContainer={editor.ProfilePopup} />
                        </div>
                )}
            </div>
        )(m.User())}
    </div>
);

let PageRightSide = SuperCritical(({ model: m } : { model : PersonProfilePage }) => 
    <div className="right-side">
        <NotificationsView model={m} />
    </div>
);

let ActionsMenu = SuperCritical(({ model : m } : { model : PersonProfilePage }) => m.ShowActionsMenu() ?
    <div className="pc-actions">
        <PCDismissible className="pc-actions-menu pc-card" onDismiss={() =>m.ShowActionsMenu(false)}>
            {m.User() &&
                <div className="action-item">
                    <div>
                        <a href={'#!reservationsdashboard?orgId=' + m.User()!.Model.OrganizationId()}>View Reservations</a>
                    </div>
                    <div className="pc-action-item-descr">
                        View all reservations for your organization.
                    </div>
                </div>
            }
            {m.User() &&
                <div className="action-item">
                    <div>
                        <a href={'#!batchesdashboard?orgId=' + m.User()!.Model.OrganizationId()}>View Batch Reservations</a>
                    </div>
                    <div className="pc-action-item-descr">
                        View all batches of repeating reservations for your organization.
                    </div>
                </div>
            }
            {m.Model.CanChangePassword() &&
                <div className="action-item">
                    <div>
                        <a href="" onClick={noDefault(() => m.ShowChangePassword(true))}>Change Password</a>
                    </div>
                    <div className="pc-action-item-descr">
                        Enter a new password for your PortCall account.
                    </div>
                </div>
            }
            {m.Model.CanAdmin() &&
                <div>
                    <div className="pc-admin-separator pc-card-admin-title">
                        Admin <div className="pc-admin-line"></div>
                    </div>
                    {(user => user &&
                        <div>
                            <div className="action-item">
                                <div>
                                    <a href="" onClick={noDefault(() => user.ShowSendPasswordReset(true))}>Send Password Reset</a>
                                </div>
                                <div className="pc-action-item-descr">
                                    Send this person a password reset link.
                                </div>
                            </div>
                            <div className="action-item">
                                <div>
                                    <a href="" onClick={noDefault(user.ShowDeleteAccount)}>Delete Account</a>
                                </div>
                                <div className="pc-action-item-descr">
                                    Remove account and block access. All history will be saved.
                                </div>
                            </div>
                        </div>
                    )(m.User())}
                </div>
            }
        </PCDismissible>
    </div> : null
);

let NotificationsView = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div className="pc-card notifications">
        <div className="pc-card-header">
            <span className="pc-card-title">
                Notifications
                {' '}
                {'(' + m.Notifications.UnviewedNotificationsCount() + ')'}
            </span>
        </div>
        {m.Notifications.Notifications().map(notif =>
            <NotificationView notif={notif} key={notif.Model.EventId()} />
        )}
    </div>
);

let NotificationView = SuperCritical(({ notif } : { notif : NotificationViewModel }) => {
    let event = notif.Event();
    if (!event) return null;
    return (
        <div className={cx('user-notification', {
                unviewed: notif.Model.WebDate() === null
            })}>
                {
                    event.Model.Type() === EventType.Comment        ? <CommentEventView event={event} /> :
                    event.Model.Type() === EventType.PortCallChange ? <PortCallChangeEventView event={event} /> :
                    // TODO: This misses many event types, most notably version events.
                    null
                }
                <span className="pc-text-smalllabel">{event.Date()}</span>
        </div>
    );
});

let CommentEventView = SuperCritical(({ event } : { event : EventViewModel }) => {
    const comment = event.Model.Comment();
    if (!comment) return null;
    const logEvent = comment.RootEvent()?.PortCalls()[0];
    return (
        <div>
            <span>{comment.Event()?.User()?.FullName() ?? comment.Event()?.Automation()?.Name()}</span>
            {' '}
            commented on your
            {' '}
            {logEvent ?
                <a href={'#!portcallprofile?id=' + logEvent.PortCallId()}>
                    portcall.
                </a> :
                // logEvent being falsey probably means the PortCall on which this comment was made has since
                // been deleted, since we delete all of the PortCall's LogEvents at that point. (Well, or it
                // could mean the server has underpopulated the navigation properties to get to RootEvent.)
                // So no live PortCall to navigate to in that case.
                // TODO: But this makes for pretty useless notification text. Could see either showing the
                // comment message, even though the PortCall has since been deleted, or else suppressing
                // this whole notification row.
                <span>portcall.</span>
            }
        </div>
    );
});

let PortCallChangeEventView = SuperCritical(({ event } : { event : EventViewModel }) => {
    let pcChange = event.PortCallChange();
    if (!pcChange) return null;
    return (
        <div>
            {pcChange.Model.Type() === PortCallChangeType.Vessel ||
             pcChange.Model.Type() === PortCallChangeType.LineTime ||
             pcChange.Model.Type() === PortCallChangeType.Berth ? <VesselTimeOrBerthChangeView pcChange={pcChange} /> :
             pcChange.Model.Type() === PortCallChangeType.Creation ? <CreationChangeView pcChange={pcChange} /> :
             pcChange.Model.Type() === PortCallChangeType.LineTimeActual ? <LineTimeActualChangeView pcChange={pcChange} /> : null}
        </div>
    );
});

let VesselTimeOrBerthChangeView = SuperCritical(({ pcChange } : { pcChange : PortCallChangeViewModel }) =>
    <div>
        <span>{pcChange.Model.Event()?.User()?.FullName() ?? pcChange.Model.Event()?.Automation()?.Name()}</span>
        {' '}
        changed the
        {pcChange.Model.Type() === PortCallChangeType.Vessel ? ' vessel ' :
         pcChange.Model.Type() === PortCallChangeType.LineTime ? ' time ' :
         pcChange.Model.Type() === PortCallChangeType.Berth ? ' location ' : ' '}
        of your
        {' '}
        <a href={'#!portcallprofile?id=' + pcChange.Model.PortCallId()}>
            portcall.
        </a>
    </div>
);

let CreationChangeView = SuperCritical(({ pcChange } : { pcChange : PortCallChangeViewModel }) =>
    <div>
        URGENT: New
        {' '}
        <a href={'#!portcallprofile?id=' + pcChange.Model.PortCallId()}>PortCall</a>
        {' '}
        at {pcChange.FirstLineTime()} for {/*pcChange.Model.VesselName()} at {pcChange.Model.BerthName()*/}.
    </div>
);

let LineTimeActualChangeView = SuperCritical(({ pcChange } : { pcChange : PortCallChangeViewModel }) =>
    <div>
        Your {pcChange.ActualDate()}
        {' '}
        <a href={'#!portcallprofile?id=' + pcChange.Model.PortCallId()}>PortCall</a>
        {' '}
        has
        {pcChange.Model.Field() === LineTimeActualField.FirstLineActual ? ' arrived.' :
        pcChange.Model.Field() === LineTimeActualField.LastLineActual ? ' departed.' : ''}
    </div>
);

let PageModals = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <div>
        <ChangePasswordView modelContainer={m.ChangePassword} />
        <SendPasswordResetModal model={m} />
        <DeleteAccountModal model={m} />
        <NotificationSettingsModal model={m} />
    </div>
);

let SendPasswordResetModal = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <PCModal className="sendpasswordreset" shown={() => !!(m.User() && m.User()!.SendPasswordResetModal())} onBackDropClick={() => {m?.User()?.ShowSendPasswordReset(false)}}>
        <div className="modal-content">
            <div className="modal-body clearfix">
                {/*<actionloadingindicator params="action: Action" />*/}
                <h2 className="modal-title">Send Password Reset</h2>
                {(user => user?.SendPasswordResetModal()?.Request.Response()?.Success &&
                    <div>
                        <div>
                            A password reset link has been sent to
                            {' '}
                            {(_u => _u &&
                                <div className="user-email">{_u.Email()}</div>
                            )(user.Model.User())}
                        </div>
                        <div>
                            <button className="pc-button" onClick={() => user.ShowSendPasswordReset(false)}>OK</button>
                        </div>
                    </div>
                )(m.User())}
            </div>
        </div>
    </PCModal>
);

let DeleteAccountModal = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <PCModal className="deleteaccount" shown={() => !!(m.User() && m.User()!.DeleteAccountModal())} onBackDropClick={() => {m?.User()?.ShowDeleteAccount(false)}}>
        <div className="modal-content">
            <div className="modal-body clearfix">
                <h2 className="modal-title">Delete Account</h2>
                {(deleteAccount => deleteAccount && (!deleteAccount.ActionSuccess() ?
                    <div>
                        {/*<actionloadingindicator params="action: Action" />*/}
                        <div>
                            Are you sure you want to delete this account?
                        </div>
                        <div className="action-btns">
                            <button className="close pc-modal-close delete-close" onClick={() => m.User()!.ShowDeleteAccount(false)}>Cancel</button>
                            <button className="pc-button" onClick={deleteAccount.Delete}>Delete</button>
                        </div>
                    </div>
                    :
                    <div>
                        <div className="confirmation-message">This account was deleted successfully.</div>
                        <div>
                            <button className="pc-button" onClick={deleteAccount.DoneDeleting}>OK</button>
                        </div>
                    </div>)
                )(m.User()?.DeleteAccountModal())}
            </div>
        </div>
    </PCModal>
);

let NotificationSettingsModal = SuperCritical(({ model: m } : { model : PersonProfilePage }) =>
    <PCModal className="notificationsettings" shown={() => !!(m.User() && m.User()!.NotificationSettingsModal())} onBackDropClick={() => {m?.User()?.ShowNotificationSettings(false)}}>
        <div className="modal-content">
            <div className="modal-header pc-modal-header">
                {(user => user &&
                    <button type="button" className="close pc-modal-close" onClick={() => user.ShowNotificationSettings(false)}><span>Close</span></button>
                )(m.User())}
                <h2 className="modal-title pc-modal-title">Notification Settings</h2>
            </div>
            <div className="modal-body">
                <div className="notification-email">
                    <label className="notification-email-label">Notification Email</label>
                    {(notifSettings => notifSettings &&
                        <input className="notification-email-input"
                            value={notifSettings.NotificationEmail() || ''}
                            onChange={e => notifSettings.NotificationEmail((e.target as HTMLInputElement).value)} />
                    )(m.User()?.NotificationSettingsModal())}
                    <div className="notification-email-descr pc-text-smalllabel">
                        Enter an email address to receive notifications. Default is your log in email.
                    </div>
                </div>
                {(user => user &&
                    <ValidationErrorView error={() => user.Validation()['NotificationEmail']} />
                )(m.User())}
            </div>
        </div>
    </PCModal>
);


// WEBPACK FOOTER //
// ./apps/portcall/personprofilepage/view.tsx